import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../../../components/card/card';
import { TenantContext } from '../../../context/tenant-context';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import './EditTemplate.scss';
import { Divider } from '../../../components/divider/divider';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import QuipliEditor from 'ckeditor';
import { useTemplateData } from './hooks/useTemplateData';
import { Tooltip } from '../../../components/tooltip/tooltip';
import SkeletonLoader from '../../../components/skeleton-loader/skeleton-loader';
import { BreadCrumb, Breadcrumb } from '../../../components/breadcrumbs/breadcrumbs';
import { toast } from '../../../util/toasts';
import { Checkbox } from '../../../components/checkbox/checkbox';

export const EditTemplate = () => {
  const { emailTemplateId } = useParams();
  const { tenant, contextPath } = useContext(TenantContext);

  const {
    templateData,
    replacementVariables,
    templateCategory,
    emailBodyValue,
    setEmailBodyValue,
    subjectValue,
    setSubjectValue,
    sendToAssignedRepresentative,
    setSendToRepresentative,
    saveTemplateData,
    isLoading,
    isSaving,
    restoreDefault,
  } = useTemplateData(emailTemplateId ?? '');

  const [editorFailed, setEditorFailed] = useState<boolean>(false);

  useEffect(() => {
    if (editorFailed) {
      toast('Failed to load editor', 'error');
    }
  }, [editorFailed]);

  const [expandPlaceholders, setExpandPlaceholders] = useState<boolean>(true);

  let breadcrumbs: Breadcrumb[] | null = null;
  if (templateData) {
    breadcrumbs = [
      { label: 'Settings' },
      { label: 'Email Customizations' },
      {
        label: `${templateData?.userType} Emails`,
        link: `${contextPath}manager/emailtemplatesv2`,
      },
      { label: 'Edit Email Template' },
    ];
  }

  return (
    <div className="edit-email-template">
      {/* Bread crumbs */}
      {breadcrumbs && <BreadCrumb breadcrumbs={breadcrumbs} />}

      <div className="edit-email-template-header gap-2">
        <a className="cursor-pointer" href={`${contextPath}manager/emailtemplatesv2`}>
          <Icon icon="Back" size="large" />
        </a>
        <Typography type="text-xl" weight="semi-bold">
          Edit Email Template
        </Typography>
      </div>

      {/* Big Body Card */}
      <Card className="card">
        {isLoading ? (
          <EditTemplateLoadingSkeleton />
        ) : (
          <div className="body">
            <div className="header">
              <Typography type="text-lg" weight="semi-bold">
                {`${templateCategory}: ${templateData?.displayTitle}`}
              </Typography>
              <div className="sub-header">
                <Typography type="text-md" className="text-grey-600 flex flex-row">
                  {templateData?.displayDescription}
                  <Tooltip tooltip={templateData?.displayTooltip} type="black" maxWidth="18rem">
                    <Icon icon="HelpCircle" />
                  </Tooltip>
                </Typography>
              </div>
              <div className="pt-4 pb-2">
                <Divider />
              </div>
            </div>

            <div className="content">
              <div className="content-header mt-2">
                <Typography type="text-md" weight="regular">
                  Email Subject
                </Typography>
                <input
                  value={subjectValue}
                  onChange={(e) => setSubjectValue(e.target.value)}
                  className="w-[-webkit-fill-available]"
                  disabled={isSaving}
                  data-cy="email-subject"
                />
                <Typography type="text-sm" className="text-grey-500">
                  This is the subject line that will appear in the recipient's inbox for this email.
                </Typography>
              </div>

              {templateData?.userType === 'Customer' && (
                <div className="content-header mt-2">
                  <div className="flex items-center gap-2">
                    <Typography type="text-md" weight="regular">
                      Send to Assigned Order Representative
                    </Typography>
                    <Checkbox
                      isChecked={sendToAssignedRepresentative}
                      onChange={(isChecked) => setSendToRepresentative(isChecked)}
                      isDisabled={isSaving}
                      data-cy="send-to-representative"
                    />
                  </div>
                  <Typography type="text-sm" className="text-grey-500">
                    If this is checked, the email will be sent to assigned order representative, no matter what the
                    representative's role is.
                  </Typography>
                </div>
              )}

              <div className="content-body mt-4">
                <Typography type="text-md" weight="regular">
                  Email Body
                </Typography>
                <Typography type="text-sm" className="text-grey-500">
                  This is the main content area of the email. Use placeholders from the table below to automatically
                  insert dynamic information.
                </Typography>

                <div className="editor-portion">
                  <div className="sub md:ml-[3rem] md:mr-[3rem] lg:ml-[6rem] lg:mr-[6rem] max-w-[99%] sm:max-w-[85%]">
                    <div className="hidden" id="ckeditor-tenant-name">
                      {tenant}::{contextPath}
                    </div>
                    <CKEditor
                      editor={QuipliEditor as any}
                      data={emailBodyValue}
                      disabled={isSaving}
                      data-cy="email-body"
                      onChange={(_, editor) => setEmailBodyValue(editor.getData())}
                      onError={(_, p) => {
                        if (!p.willEditorRestart) {
                          setTimeout(() => setEditorFailed(true), 100);
                        }
                      }}
                    />
                    <div className="submit-actions gap-2 mt-4">
                      <button
                        className="text-white bg-black"
                        disabled={isSaving}
                        onClick={restoreDefault}
                        data-cy="email-template-restore"
                      >
                        <Icon icon="Refresh" color="white" />
                        <Typography type="text-xs" weight="regular">
                          Restore Default
                        </Typography>
                      </button>
                      <button
                        className="text-black bg-quipli-yellow-200"
                        onClick={saveTemplateData}
                        disabled={isSaving}
                        data-cy="email-template-save"
                      >
                        <Icon icon="FloppySave" color="black" />
                        <Typography type="text-xs" weight="regular">
                          Save Changes
                        </Typography>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 mb-2">
                <Divider />
              </div>
              <div className="content-placeholders">
                <Typography type="text-lg" weight="semi-bold">
                  Email Template Placeholders
                </Typography>
                <Typography type="text-sm" className="text-grey-500">
                  Use the placeholders below to automatically insert dynamic information into your email templates. When
                  the email is sent, each placeholder will be replaced with the relevant data, such as a customer's name
                  or order details. Simply copy and paste the placeholder into your email subject or body where you want
                  the information to appear.
                </Typography>

                <div className={`placeholder-grid grid grid-cols-1 md:grid-cols-10 mt-4 `}>
                  {/* Header Row */}
                  <div className="top-left cell-common col-span-1 md:col-span-3 bg-gray-200 p-2">
                    Email Template Placeholder
                  </div>
                  <div className="top-right flex flex-row justify-between cell-common col-span-1 md:col-span-7 bg-gray-200 p-2">
                    <p>Description</p>
                    <div className="cursor-pointer" onClick={() => setExpandPlaceholders((cto) => !cto)}>
                      <Icon icon={expandPlaceholders ? 'ChevronUp' : 'ChevronDown'} />
                    </div>
                  </div>
                  {/* Data Rows */}
                  {expandPlaceholders &&
                    Object.entries(replacementVariables).map(([key, value], index) => (
                      <React.Fragment key={index}>
                        <div className="cell-common standard-cell col-span-1 md:col-span-3 p-2 text-grey-700">
                          {`{${key}}`}
                        </div>
                        <div className="cell-common standard-cell col-span-1 md:col-span-7 p-2 text-grey-700">
                          {!value || value == '' ? 'N/A' : value}
                        </div>
                      </React.Fragment>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

const EditTemplateLoadingSkeleton = () => (
  <div className="content">
    <div className="header p-1 mt-0">
      <SkeletonLoader style={{ width: '100%', height: '5rem' }} />
    </div>
    <Divider />
    <div className="body">
      <SkeletonLoader num={2} style={{ maxWidth: '100%', height: '8rem', margin: '0.5rem' }} />
    </div>
  </div>
);
